/**
 * Página global "Destilados" (Fase 27).
 *
 * Lista paginada de jobs session.distill con filtro por modo. Cada row es
 * un link al SessionDetail con el span del job ya seleccionado para hacer
 * deep-link al waterfall.
 */
import { type ReactElement, type MouseEvent } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchDistillHistory, type DistillHistoryItem } from '../../api/admin/distill.js';
import { formatDurationMs, formatNumber, nanoToUsdCompact, shortId } from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';

/** Evita que el click en un link interno (user/session) dispare la navegación de la fila. */
function stopRowClick(e: MouseEvent): void {
  e.stopPropagation();
}

type ModeFilter = 'all' | 'oneshot' | 'agentic';

function ModeChip({ mode }: { mode: 'oneshot' | 'agentic' }): ReactElement {
  const cls = mode === 'agentic' ? 'badge b-warn' : 'badge b-info';
  return <span className={cls}>{mode}</span>;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const delta = Math.max(0, now - then);
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return new Date(iso).toLocaleString();
}

export function DistillPage(): ReactElement {
  const [mode, setMode] = useState<ModeFilter>('all');
  const navigate = useNavigate();

  const filters = mode === 'all' ? {} : { mode };
  const query = useQuery({
    queryKey: ['admin', 'distill-history', mode],
    queryFn: () => fetchDistillHistory({ ...filters, limit: 50 }),
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Destilados</h1>
          <p className="page-sub">
            Historial global del job <code className="mono">session.distill</code> ·
            refresh 30s
          </p>
        </div>
      </div>

      <section className="sess-filters" data-testid="distill-filters">
        <span className="label">Modo</span>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {(['all', 'oneshot', 'agentic'] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`chip${mode === m ? ' is-on' : ''}`}
              onClick={() => setMode(m)}
              data-testid={`chip-mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
      </section>

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <section className="card" style={{ padding: 0 }}>
          {query.data.items.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              No hay destilados registrados con este filtro.
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Modo</th>
                  <th>User</th>
                  <th>Módulo</th>
                  <th style={{ textAlign: 'right' }}>Duración</th>
                  <th style={{ textAlign: 'right' }}>Costo</th>
                  <th style={{ textAlign: 'right' }}>Facts</th>
                  <th>Session</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((it: DistillHistoryItem) => (
                  <tr
                    key={it.jobSpanId}
                    onClick={() => it.sessionId && navigate(`/distill/${it.sessionId}`)}
                    style={{ cursor: it.sessionId ? 'pointer' : 'default' }}
                    data-testid="distill-row"
                    aria-label="Ver detalle del destilado"
                  >
                    <td>
                      <span title={it.startedAt}>{relativeTime(it.startedAt)}</span>
                    </td>
                    <td>
                      <ModeChip mode={it.mode} />
                    </td>
                    <td>
                      {it.userId ? (
                        <Link to={`/users/${it.userId}`} onClick={stopRowClick}>
                          {it.userEmail ?? shortId(it.userId, 10)}
                        </Link>
                      ) : (
                        <span className="t-meta">—</span>
                      )}
                    </td>
                    <td className="mono">{it.moduleSlug ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{formatDurationMs(it.durationMs)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {nanoToUsdCompact(preferRawCost(it.totalCostRawNano, it.totalCostNano))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {it.factsAccepted !== null ? formatNumber(it.factsAccepted) : '—'}
                    </td>
                    <td>
                      {it.sessionId ? (
                        <Link
                          to={`/sessions/${it.sessionId}?span=${it.jobSpanId}`}
                          className="mono"
                          onClick={stopRowClick}
                        >
                          {shortId(it.sessionId, 10)}
                        </Link>
                      ) : (
                        <span className="t-meta">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </>
  );
}
